import { memo, useLayoutEffect, useRef } from 'react'

/**
 * Fast lightweight markdown-to-HTML for streaming.
 *
 * Used while the response is mid-stream — we render headers, bold, italic,
 * lists, blockquotes, and line breaks without paying the cost of
 * ReactMarkdown + remark-gfm on every chunk. Once streaming finishes the
 * caller swaps this out for the full <MarkdownMessage> with theological
 * term + scripture popovers.
 *
 * Source-of-truth for the styling is duplicated with MarkdownMessage's
 * Tailwind component overrides — keep them in sync.
 */
function lightMarkdown(text) {
  if (!text) return ''
  return (
    text
      // Escape HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Headers (must be at line start)
      .replace(
        /^#### (.+)$/gm,
        '<h4 class="text-sm font-semibold text-[var(--gold)]/80 mt-2 mb-1">$1</h4>'
      )
      .replace(
        /^### (.+)$/gm,
        '<h3 class="text-base font-semibold font-heading text-[var(--gold)] mt-2.5 mb-1">$1</h3>'
      )
      .replace(
        /^## (.+)$/gm,
        '<h2 class="text-lg font-bold font-heading text-[var(--gold)] mt-3 mb-1.5">$1</h2>'
      )
      .replace(
        /^# (.+)$/gm,
        '<h1 class="text-xl font-bold font-heading text-[var(--gold)] mt-4 mb-2">$1</h1>'
      )
      // Blockquotes (note: '>' was already HTML-escaped to '&gt;' above)
      .replace(
        /^&gt; (.+)$/gm,
        '<blockquote class="border-l-2 border-[var(--gold)]/40 pl-3 my-1 italic text-[var(--text-muted)]">$1</blockquote>'
      )
      // Bold + italic combined
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      // Bold
      .replace(
        /\*\*(.+?)\*\*/g,
        '<strong class="font-semibold text-[var(--text-primary)]">$1</strong>'
      )
      // Italic
      .replace(/\*(.+?)\*/g, '<em class="italic opacity-90">$1</em>')
      // Unordered lists
      .replace(/^- (.+)$/gm, '<li class="ml-5 list-disc text-sm leading-relaxed">$1</li>')
      // Ordered lists
      .replace(/^\d+\. (.+)$/gm, '<li class="ml-5 list-decimal text-sm leading-relaxed">$1</li>')
      // Line breaks (double newline = paragraph break, single = <br>)
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>')
  )
}

const StreamingContent = memo(function StreamingContent({ content }) {
  const containerRef = useRef(null)
  useLayoutEffect(() => {
    if (!containerRef.current) return
    containerRef.current.innerHTML = lightMarkdown(content)
  }, [content])
  // A11Y-001: this component is only rendered while a voice is mid-stream
  // (callers swap to MarkdownMessage on status === 'done'). Marking the
  // container aria-hidden prevents the parent thread's aria-live region
  // from announcing every per-token chunk — assistive tech goes hostile
  // otherwise. The "finished responding" announcement comes from the
  // per-turn announcer in DesktopTurnBlock + mobile TurnBlock once
  // voicesAllDone flips true, and MarkdownMessage (visible to SR) takes
  // over from there with the final content.
  return (
    <div
      ref={containerRef}
      className="markdown-message text-sm leading-relaxed"
      aria-hidden="true"
    />
  )
})

export default StreamingContent
